import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '../client';
import type {
    CreatePersonalAccessTokenRequest,
    CreatePersonalAccessTokenResponse,
    PersonalAccessTokensResponse,
} from '../types';

const PERSONAL_ACCESS_TOKENS_KEY = ['personalAccessTokens'] as const;

/**
 * Lists the active personal access tokens of the current user.
 */
export function usePersonalAccessTokens(): UseQueryResult<PersonalAccessTokensResponse, Error> {
    return useQuery({
        queryKey: PERSONAL_ACCESS_TOKENS_KEY,
        queryFn: () => api.get<PersonalAccessTokensResponse>('/api/account/tokens'),
    });
}

/**
 * Creates a personal access token.
 * The plaintext token is part of the response and is never returned again.
 */
export function useCreatePersonalAccessToken(): UseMutationResult<
    CreatePersonalAccessTokenResponse,
    Error,
    CreatePersonalAccessTokenRequest
> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreatePersonalAccessTokenRequest) =>
            api.post<CreatePersonalAccessTokenResponse>('/api/account/tokens', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PERSONAL_ACCESS_TOKENS_KEY });
        },
    });
}

/**
 * Revokes a personal access token and drops it from the cached list.
 */
export function useRevokePersonalAccessToken(): UseMutationResult<void, Error, number> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => api.delete<void>(`/api/account/tokens/${id}`),
        onSuccess: (_, revokedId) => {
            queryClient.setQueryData<PersonalAccessTokensResponse>(
                PERSONAL_ACCESS_TOKENS_KEY,
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        items: old.items.filter((token) => token.id !== revokedId),
                    };
                }
            );
        },
    });
}
